import { getName } from 'i18n-iso-countries';
import { getSnapshot } from 'mobx-state-tree';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Button,
  Checkbox,
  ControlGroup,
  Dialog,
  EmptyInputGroup,
  FormField,
  Grid,
  Heading,
  IDialogProps,
  InputGroup,
  majorScale,
  NumberInput,
  useToast,
} from '../../../npaper';
import {
  CountrySelect,
  flagEmojiFromCode,
} from '../../../npaper/CountrySelect';
import { IVatRate, useStores } from '../../../stores';

export interface IVatRateDialogProps
  extends Omit<Omit<IDialogProps, 'onClose'>, 'title'> {
  vatRate?: IVatRate;
  onClose: (contact?: IVatRate) => void;
}

export const VatRateDialog: React.FC<IVatRateDialogProps> = ({
  vatRate,
  onClose,
  ...props
}) => {
  const intl = useIntl();
  const toaster = useToast();
  const { vatRateStore } = useStores();
  const [isShown, setIsShown] = useState(props.isShown);
  const [createdVatRate, setCreatedVatRate] = useState<IVatRate>();
  useEffect(() => {
    setIsShown(props.isShown);
  }, [props.isShown]);

  const { register, reset, control, errors, handleSubmit, formState } =
    useForm();

  useEffect(() => {
    if (vatRate) {
      reset(getSnapshot(vatRate));
    } else {
      reset({
        rate: 0,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vatRate]);

  const close = () => {
    setIsShown(false);
  };

  const onSubmit = async (values: any /** TODO: Change type */) => {
    if (vatRate) {
      return vatRateStore
        .updateItem(vatRate, values)
        .then(() => close())
        .catch(toaster.showDefaultError);
    } else {
      return vatRateStore
        .createItem(values)
        .then((rate) => {
          setCreatedVatRate(rate);
          close();
        })
        .catch(toaster.showDefaultError);
    }
  };

  return (
    <Dialog
      {...props}
      title={
        vatRate
          ? intl.formatMessage({ defaultMessage: 'Edit VAT rate' })
          : intl.formatMessage({ defaultMessage: 'Create VAT rate' })
      }
      onClose={() => onClose(createdVatRate)}
      isShown={isShown}
      actions={
        <>
          <Button onClick={() => close()} disabled={formState.isSubmitting}>
            <FormattedMessage defaultMessage="Cancel" />
          </Button>
          <Button
            primary
            onClick={handleSubmit(onSubmit)}
            isLoading={formState.isSubmitting}
          >
            {vatRate
              ? intl.formatMessage({ defaultMessage: 'Save changes' })
              : intl.formatMessage({ defaultMessage: 'Create VAT rate' })}
          </Button>
        </>
      }
    >
      <Grid padding={majorScale(2)}>
        <Heading size={300} className="col-span-12">
          <FormattedMessage defaultMessage="Details" />
        </Heading>

        <FormField
          className="col-span-9"
          label={intl.formatMessage({ defaultMessage: 'Code' })}
          inline={true}
        >
          <InputGroup
            placeholder={intl.formatMessage({
              defaultMessage: 'VAT rate code',
            })}
            ref={register({
              required: intl.formatMessage({
                defaultMessage: 'Please enter a code',
              }),
              maxLength: {
                value: 64,
                message: intl.formatMessage({
                  defaultMessage: 'Code must be 64 characters or less',
                }),
              },
            })}
            name="code"
            isInvalid={!!errors.code}
            validationMessage={errors.code && errors.code.message}
          />
        </FormField>

        <FormField
          className="col-span-9"
          label={intl.formatMessage({ defaultMessage: 'Rate' })}
          inline={true}
          isInvalid={errors.rate}
          hint={
            (errors.rate && errors.rate.message) ||
            (vatRate &&
              intl.formatMessage({
                defaultMessage: 'VAT rate cannot be changed',
              }))
          }
        >
          <ControlGroup className="mb-1">
            <Controller
              control={control}
              name="rate"
              rules={{
                required: intl.formatMessage({
                  defaultMessage: 'Please enter the VAT rate in percentage',
                }),
              }}
              render={({ onChange, value }) => (
                <NumberInput
                  placeholder={intl.formatMessage({
                    defaultMessage: 'The VAT rate in percentage',
                  })}
                  readOnly={!!vatRate}
                  onChange={(n) => onChange(n / 100)}
                  value={value * 100}
                  isInvalid={!!errors.rate}
                />
              )}
            />
            <EmptyInputGroup>%</EmptyInputGroup>
          </ControlGroup>
        </FormField>

        <FormField
          className="col-span-9"
          label={intl.formatMessage({ defaultMessage: 'Display name' })}
          inline={true}
        >
          <InputGroup
            placeholder={intl.formatMessage({
              defaultMessage: 'Display name, e.g. VAT',
            })}
            ref={register({
              required: intl.formatMessage({
                defaultMessage: 'Enter the display name of the VAT rate',
              }),
              maxLength: {
                value: 64,
                message: intl.formatMessage({
                  defaultMessage: 'Display name must be 64 characters or less',
                }),
              },
            })}
            name="display_name"
            isInvalid={!!errors.display_name}
            validationMessage={
              (errors.display_name && errors.display_name.message) ||
              intl.formatMessage({
                defaultMessage: 'The display name is visible on invoices',
              })
            }
          />
        </FormField>
        <FormField
          className="col-span-9"
          label={intl.formatMessage({ defaultMessage: 'Name' })}
          inline={true}
        >
          <InputGroup
            placeholder={intl.formatMessage({
              defaultMessage: 'A short description name, e.g. Danish sales VAT',
            })}
            ref={register({
              required: intl.formatMessage({
                defaultMessage: 'Please give your VAT rate a short description',
              }),
              maxLength: {
                value: 64,
                message: intl.formatMessage({
                  defaultMessage: 'Name must be 64 characters or less',
                }),
              },
            })}
            name="name"
            isInvalid={!!errors.display_name}
            validationMessage={
              (errors.display_name && errors.display_name.message) ||
              intl.formatMessage({
                defaultMessage: 'The name is only visible to you',
              })
            }
          />
        </FormField>

        <FormField
          className="col-span-9"
          label={intl.formatMessage({ defaultMessage: 'Apply to' })}
          inline
        >
          <Controller
            control={control}
            name="require_vat_number"
            defaultValue={true}
            render={({ onChange, value }) => (
              <Checkbox
                checked={value}
                onChange={onChange}
                label={intl.formatMessage({
                  defaultMessage: 'Customers with a VAT number',
                })}
              />
            )}
          />
          <Controller
            control={control}
            name="require_vat_number"
            defaultValue={true}
            render={({ onChange, value }) => (
              <Checkbox
                checked={value}
                onChange={onChange}
                label={intl.formatMessage({
                  defaultMessage: 'Goods',
                })}
              />
            )}
          />
          <Controller
            control={control}
            name="require_vat_number"
            defaultValue={true}
            render={({ onChange, value }) => (
              <Checkbox
                checked={value}
                onChange={onChange}
                label={intl.formatMessage({
                  defaultMessage: 'Services',
                })}
              />
            )}
          />
        </FormField>

        <FormField
          className="col-span-9"
          label={intl.formatMessage({ defaultMessage: 'Apply in' })}
          inline
        >
          <Controller
            control={control}
            name="countries"
            render={({ onChange, value }) => (
              <>
                <CountrySelect
                  placeholder={intl.formatMessage({
                    defaultMessage: 'Select one or more countries',
                  })}
                  onChange={(code) => {
                    const newValue = [...(value || [])];
                    const index = newValue.indexOf(code);
                    if (index === -1) {
                      newValue.push(code);
                    }
                    onChange(newValue);
                  }}
                />
                {value?.map((code: string) => (
                  <ControlGroup width="100%" key={code}>
                    <EmptyInputGroup width="100%" height={32}>
                      {flagEmojiFromCode(code)} {getName(code, 'da')}
                    </EmptyInputGroup>
                    <Button
                      iconLeft="cross"
                      onClick={() => {
                        const newValue = [...(value || [])];
                        const index = newValue.indexOf(code);
                        if (index > -1) {
                          newValue.splice(index, 1);
                        }
                        onChange(newValue);
                      }}
                    />
                  </ControlGroup>
                ))}
              </>
            )}
          />
        </FormField>

        <FormField
          className="col-span-9"
          label={intl.formatMessage({ defaultMessage: 'Do not apply in' })}
          inline
        >
          <Controller
            control={control}
            name="exclude_countries"
            render={({ onChange, value }) => (
              <>
                <CountrySelect
                  placeholder={intl.formatMessage({
                    defaultMessage: 'Select one or more countries',
                  })}
                  onChange={(code) => {
                    const newValue = [...(value || [])];
                    const index = newValue.indexOf(code);
                    if (index > -1) {
                      newValue.splice(index, 1);
                    } else {
                      newValue.push(code);
                    }
                    onChange(newValue);
                  }}
                />
                {value?.map((code: string) => (
                  <ControlGroup width="100%" key={code}>
                    <EmptyInputGroup width="100%" height={32}>
                      {flagEmojiFromCode(code)} {getName(code, 'da')}
                    </EmptyInputGroup>
                    <Button
                      iconLeft="cross"
                      onClick={() => {
                        const newValue = [...(value || [])];
                        const index = newValue.indexOf(code);
                        if (index > -1) {
                          newValue.splice(index, 1);
                        }
                        onChange(newValue);
                      }}
                    />
                  </ControlGroup>
                ))}
              </>
            )}
          />
        </FormField>
      </Grid>
    </Dialog>
  );
};
